import { useEffect, useRef, useState } from 'react';
import {
  EV_COLOR,
  PHASE_COLORS,
  PHASE_LABEL,
  PHASE_TEXT_C,
  STATUS_COLOR,
  MON3,
  SEV_COLOR,
  expandOccurrences,
  fmtShort,
  isNonWorking,
  pctOf,
  pd,
  today,
  utilColor,
  type EccBounds,
  type EccDeliverable,
  type EccEvent,
  type EccPhase,
  type EccRisk,
  type EccSpan,
} from './eccLib';
import { pmGet } from '../../api';

/**
 * The Execution Control Center's right-hand panes.
 *
 * Cloned from the `ganttHtml`/`drawGantt`, `calendarHtml`, `governanceHtml`, `riskHtml` and
 * `brTabHtml`/`brTabContent` functions of
 * `/Users/glucic/src/ai_readiness_template/views/roadmap-execution/tracker.ejs`.
 *
 * SEPARATE FILE, SAME SCREEN. The source keeps these as functions inside one view; here
 * they are components in a sibling module so neither file becomes unreadable. That is file
 * organisation, not a redesign — the panes are not shared with anything and are not
 * parameterised across screens (CLAUDE.md §1.2.1).
 */

/* ===================== GANTT ===================== */

interface GanttProps {
  phases: EccPhase[];
  phaseBounds: Record<string, EccBounds>;
  deliverables: EccDeliverable[];
}

/**
 * A CANVAS Gantt, drawn imperatively, exactly as the source draws it.
 *
 * Kept as canvas rather than reimplemented as SVG or divs: the shading of every non-working
 * day across a multi-year span is thousands of rectangles, which is what canvas is for, and
 * "modernising" it is precisely what §1.2 rule 2 forbids.
 *
 * `ppd` — pixels per day — is 7 × zoom, or the pane width divided by the span when zoom is
 * 'fit'. The bar is drawn twice: the full span at 38% alpha, then the completed fraction
 * solid on top.
 */
export function EccGantt({ phases, phaseBounds, deliverables }: GanttProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sideRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState<number | 'fit'>(1);

  const rows = phases
    .map((ph) => ({ key: ph.key, label: PHASE_LABEL[ph.key] || ph.label, bd: phaseBounds[ph.key] }))
    .filter((r) => r.bd && r.bd.start && r.bd.end);

  const phasePct = (key: string) => pctOf(deliverables.filter((x) => x.phase === key));

  useEffect(() => {
    const canvas = canvasRef.current;
    const side = sideRef.current;
    if (!canvas || !side || !rows.length) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const headerH = 34;
    const rowH = 36;
    const barH = 18;

    const dates: Date[] = [];
    rows.forEach((r) => {
      const s = pd(r.bd!.start);
      const e = pd(r.bd!.end);
      if (s) dates.push(s);
      if (e) dates.push(e);
    });
    const t = today();
    dates.push(t);
    let minDate = new Date(Math.min(...dates.map((d) => d.getTime())));
    const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())));
    minDate.setDate(minDate.getDate() - 3);
    maxDate.setDate(maxDate.getDate() + 6);
    minDate = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    const totalDays = Math.ceil((maxDate.getTime() - minDate.getTime()) / 86400000);

    let ppd: number;
    if (zoom === 'fit') {
      const availW = (canvas.parentElement?.clientWidth || 700) - 2;
      ppd = Math.max(0.5, availW / Math.max(1, totalDays));
    } else {
      ppd = 7 * zoom;
    }
    const canvasW = Math.max(totalDays * ppd, 400);
    const canvasH = headerH + rows.length * rowH;
    canvas.width = canvasW;
    canvas.height = canvasH;
    ctx.clearRect(0, 0, canvasW, canvasH);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvasW, canvasH);

    for (let d = 0; d < totalDays; d++) {
      const dt = new Date(minDate);
      dt.setDate(dt.getDate() + d);
      if (isNonWorking(dt)) {
        ctx.fillStyle = 'rgba(140,140,140,0.07)';
        ctx.fillRect(d * ppd, headerH, ppd, canvasH - headerH);
      }
    }
    ctx.fillStyle = '#f5f7f9';
    ctx.fillRect(0, 0, canvasW, headerH);
    ctx.fillStyle = '#546e7a';
    ctx.font = 'bold 10px Arial';
    ctx.textAlign = 'center';
    let cm = minDate.getMonth();
    let mStart = 0;
    for (let d = 0; d <= totalDays; d++) {
      const dt = new Date(minDate);
      dt.setDate(dt.getDate() + d);
      if (dt.getMonth() !== cm || d === totalDays) {
        const x = mStart * ppd + ((d - mStart) * ppd) / 2;
        const mn = new Date(minDate.getTime() + mStart * 86400000).toLocaleDateString('en-US', {
          month: 'short',
          year: '2-digit',
        });
        ctx.fillText(mn, x, 21);
        cm = dt.getMonth();
        mStart = d;
      }
    }

    let sideHTML = `<div style="height:${headerH}px;border-bottom:2px solid #e3e8ec;background:#f5f7f9;display:flex;align-items:center;padding:0 10px;font-size:11px;font-weight:700;text-transform:uppercase;color:#78909c;">Phase</div>`;
    rows.forEach((r, i) => {
      const y = headerH + i * rowH;
      const col = PHASE_COLORS[r.key] || '#90a4ae';
      const pct = phasePct(r.key);
      sideHTML += `<div style="height:${rowH}px;display:flex;align-items:center;gap:8px;padding:0 10px;border-bottom:1px solid #f0f0f0;font-size:12px;color:#37474f;"><span style="width:10px;height:10px;border-radius:3px;background:${col};flex-shrink:0;"></span><span style="flex:1;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;">${r.label}</span><span style="font-size:11px;font-weight:700;color:${col};">${pct}%</span></div>`;
      const barY = y + (rowH - barH) / 2;
      const s = pd(r.bd!.start)!;
      const e = pd(r.bd!.end)!;
      const sOff = (s.getTime() - minDate.getTime()) / 86400000;
      const eOff = (e.getTime() - minDate.getTime()) / 86400000;
      const px = sOff * ppd;
      const pw = Math.max((eOff - sOff + 1) * ppd, 2);
      ctx.globalAlpha = 0.38;
      ctx.fillStyle = col;
      ctx.fillRect(px, barY, pw, barH);
      ctx.globalAlpha = 1;
      ctx.fillStyle = col;
      ctx.fillRect(px, barY, Math.max((pw * pct) / 100, pct > 0 ? 2 : 0), barH);
      ctx.strokeStyle = 'rgba(0,0,0,.12)';
      ctx.strokeRect(px + 0.5, barY + 0.5, pw - 1, barH - 1);
      ctx.strokeStyle = '#f0f0f0';
      ctx.beginPath();
      ctx.moveTo(0, y + rowH);
      ctx.lineTo(canvasW, y + rowH);
      ctx.stroke();
    });
    side.innerHTML = sideHTML;

    const tOff = (t.getTime() - minDate.getTime()) / 86400000;
    if (tOff >= 0 && tOff <= totalDays) {
      const tx = tOff * ppd;
      ctx.strokeStyle = '#e53935';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(tx, headerH);
      ctx.lineTo(tx, canvasH);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = '#e53935';
      ctx.font = 'bold 9px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Today', Math.max(18, Math.min(tx, canvasW - 18)), 31);
    }
  });

  if (!rows.length) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#90a4ae' }}>
        No phase dates available for this project.
      </div>
    );
  }

  const starts = rows.map((r) => pd(r.bd!.start)!).filter(Boolean);
  const ends = rows.map((r) => pd(r.bd!.end)!).filter(Boolean);
  const progStart = new Date(Math.min(...starts.map((d) => d.getTime())));
  const progEnd = new Date(Math.max(...ends.map((d) => d.getTime())));
  const bizBetween = (a: Date, b: Date) => {
    if (b < a) return 0;
    let n = 0;
    const dd = new Date(a.getFullYear(), a.getMonth(), a.getDate());
    const bb = new Date(b.getFullYear(), b.getMonth(), b.getDate());
    while (dd <= bb) {
      if (!isNonWorking(dd)) n++;
      dd.setDate(dd.getDate() + 1);
    }
    return n;
  };
  const totalCal = Math.round((progEnd.getTime() - progStart.getTime()) / 86400000) + 1;
  const totalBiz = bizBetween(progStart, progEnd);
  const tg = today();
  const bizLeft = tg > progEnd ? 0 : bizBetween(tg < progStart ? progStart : tg, progEnd);
  const fmtFull = (dt: Date) =>
    dt.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });

  const chip = (ic: string, lb: string, vl: string, cl: string, sb: string) => (
    <div
      key={lb}
      style={{
        flex: 1,
        minWidth: 175,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: '#fff',
        border: '1px solid #eceff1',
        borderRadius: 10,
        padding: '12px 16px',
        boxShadow: '0 1px 3px rgba(0,0,0,.05)',
      }}
    >
      <i className="material-icons" style={{ fontSize: 28, color: cl }}>
        {ic}
      </i>
      <div>
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '.04em',
            color: '#90a4ae',
          }}
        >
          {lb}
        </div>
        <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#263238' }}>{vl}</div>
        {sb ? <div style={{ fontSize: 11, color: '#90a4ae' }}>{sb}</div> : null}
      </div>
    </div>
  );

  const zb: React.CSSProperties = {
    width: 26,
    height: 24,
    border: '1px solid #cfd8dc',
    background: '#fff',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 700,
    color: '#546e7a',
  };
  const zoomBy = (dir: number) => {
    const cur = zoom === 'fit' || !zoom ? 1 : zoom;
    setZoom(dir === 0 ? 1 : Math.max(0.25, Math.min(6, cur * (dir > 0 ? 1.4 : 1 / 1.4))));
  };

  return (
    <div ref={wrapRef} style={{ padding: '6px 4px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          justifyContent: 'flex-end',
          margin: '0 4px 10px',
        }}
      >
        <span style={{ fontSize: 11, color: '#999' }}>Zoom</span>
        <button onClick={() => zoomBy(-1)} title="Zoom out" style={zb}>
          −
        </button>
        <span style={{ fontSize: 11, color: '#607d8b', minWidth: 40, textAlign: 'center' }}>
          {zoom === 'fit' ? 'Fit' : `${Math.round(zoom * 100)}%`}
        </span>
        <button onClick={() => zoomBy(1)} title="Zoom in" style={zb}>
          +
        </button>
        <button
          onClick={() => setZoom('fit')}
          title="Fit the whole timeline on one screen"
          style={{
            height: 24,
            padding: '0 10px',
            border: '1px solid #cfd8dc',
            background: '#fff',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 600,
            color: '#546e7a',
          }}
        >
          Fit
        </button>
      </div>
      <div
        style={{
          display: 'flex',
          border: '1px solid #e3e8ec',
          borderRadius: 8,
          overflow: 'hidden',
          background: '#fff',
        }}
      >
        <div
          ref={sideRef}
          style={{
            width: 210,
            minWidth: 210,
            borderRight: '2px solid #e3e8ec',
            background: '#fafbfc',
          }}
        />
        <div style={{ flex: 1, overflowX: 'auto' }}>
          <canvas ref={canvasRef} />
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          gap: 18,
          alignItems: 'center',
          margin: '10px 4px 0',
          fontSize: 11.5,
          color: '#78909c',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span
            style={{ display: 'inline-block', width: 22, height: 9, borderRadius: 3, background: '#90caf9' }}
          />
          Phase span
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span
            style={{ display: 'inline-block', width: 22, height: 9, borderRadius: 3, background: '#1976d2' }}
          />
          % complete
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ display: 'inline-block', width: 0, height: 14, borderLeft: '2px dashed #e53935' }} />
          Today
        </span>
      </div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 16 }}>
        {chip('play_circle', 'Start Date', fmtFull(progStart), '#1976d2', '')}
        {chip('flag', 'End Date', fmtFull(progEnd), '#8e24aa', '')}
        {chip('date_range', 'Project Duration', `${totalCal} days`, '#455a64', `${totalBiz} business days`)}
        {chip(
          'hourglass_bottom',
          'Business Days Left',
          `${bizLeft} days`,
          bizLeft <= 10 ? '#e53935' : '#43a047',
          tg > progEnd ? 'program has ended' : tg < progStart ? 'not started yet' : 'until end',
        )}
      </div>
    </div>
  );
}

/* ===================== CALENDAR ===================== */

interface CalendarProps {
  calY: number;
  calM: number;
  phases: EccPhase[];
  phaseBounds: Record<string, EccBounds>;
  phaseSpans: EccSpan[];
  allOppsMode: boolean;
  events: EccEvent[];
  onMove: (n: number) => void;
  onToday: () => void;
  onOpenGovernance: () => void;
}

/**
 * The month grid, with continuous phase bands, auto phase-end milestones and events.
 *
 * BANDS SKIP NON-WORKING DAYS, and the rounded corners are computed from whether the
 * PREVIOUS and NEXT working days are still inside the band — so a band visibly closes on
 * Friday and reopens on Monday rather than running through the weekend. That is the source's
 * `rl`/`rr` logic and it is what makes the calendar readable.
 *
 * In 'All opportunities' scope the bands come from `phase_spans` — one per opportunity per
 * phase, each labelled with its opportunity id — so overlapping phases stay distinguishable
 * instead of merging into one smear.
 */
export function EccCalendar({
  calY,
  calM,
  phases,
  phaseBounds,
  phaseSpans,
  allOppsMode,
  events,
  onMove,
  onToday,
  onOpenGovernance,
}: CalendarProps) {
  const wheelLock = useRef(false);

  const calBands = () => {
    if (allOppsMode && phaseSpans && phaseSpans.length) {
      return phaseSpans
        .filter((p) => p.start && p.end)
        .map((p) => ({
          phaseKey: p.key,
          opp: p.opp,
          s: p.start,
          e: p.end,
          color: PHASE_COLORS[p.key] || '#90a4ae',
          label: `${PHASE_LABEL[p.key] || p.key} · ${p.opp}`,
        }));
    }
    return phases
      .filter((ph) => {
        const bd = phaseBounds[ph.key];
        return bd && bd.start && bd.end;
      })
      .map((ph) => {
        const bd = phaseBounds[ph.key]!;
        return {
          phaseKey: ph.key,
          opp: null as string | null,
          s: bd.start!,
          e: bd.end!,
          color: PHASE_COLORS[ph.key] || '#90a4ae',
          label: PHASE_LABEL[ph.key] || ph.label,
        };
      });
  };

  const first = new Date(calY, calM, 1);
  const startDow = first.getDay();
  const dim = new Date(calY, calM + 1, 0).getDate();
  const t = today();
  const inMo = (d: Date | null) => !!d && d.getFullYear() === calY && d.getMonth() === calM;

  const byDay: Record<number, { ev: EccEvent[]; mile: { title: string; color: string; auto?: boolean }[] }> = {};
  const cellOf = (dd: number) => (byDay[dd] ??= { ev: [], mile: [] });

  events.forEach((e) => {
    expandOccurrences(e).forEach((ds) => {
      const d = pd(ds);
      if (inMo(d)) {
        if (e.event_type === 'Milestone') cellOf(d!.getDate()).mile.push({ title: e.title, color: '#ef6c00' });
        else cellOf(d!.getDate()).ev.push(e);
      }
    });
  });

  const cb = calBands();
  cb.forEach((bn) => {
    if (!bn.e) return;
    const d = pd(bn.e);
    if (inMo(d))
      cellOf(d!.getDate()).mile.push({
        title: `${PHASE_LABEL[bn.phaseKey] || bn.phaseKey} end${bn.opp ? ` · ${bn.opp}` : ''}`,
        color: bn.color,
        auto: true,
      });
  });

  const cells: JSX.Element[] = [];
  for (let i = 0; i < startDow; i++) cells.push(<div className="cal-cell muted" key={`m${i}`} />);
  for (let day = 1; day <= dim; day++) {
    const isToday = t.getFullYear() === calY && t.getMonth() === calM && t.getDate() === day;
    const b = byDay[day] || { ev: [], mile: [] };
    const dt2 = new Date(calY, calM, day);
    const nonWork = isNonWorking(dt2);
    const bands = nonWork
      ? []
      : cb
          .filter((bn) => {
            const s = pd(bn.s);
            const e = pd(bn.e);
            return s && e && dt2 >= s && dt2 <= e;
          })
          .map((bn, bi) => {
            const s = pd(bn.s)!;
            const e = pd(bn.e)!;
            const prev = new Date(dt2);
            prev.setDate(prev.getDate() - 1);
            const nxt = new Date(dt2);
            nxt.setDate(nxt.getDate() + 1);
            const prevBand = prev >= s && prev <= e && !isNonWorking(prev);
            const nextBand = nxt >= s && nxt <= e && !isNonWorking(nxt);
            const rl = !prevBand;
            const rr = !nextBand;
            return (
              <div
                key={bi}
                className="cal-band"
                style={{
                  background: bn.color,
                  borderRadius: `${rl ? '5px' : '0'} ${rr ? '5px' : '0'} ${rr ? '5px' : '0'} ${rl ? '5px' : '0'}`,
                }}
                title={bn.label}
              >
                {rl ? bn.label : ' '}
              </div>
            );
          });
    cells.push(
      <div className={`cal-cell ${isToday ? 'today' : ''} ${nonWork ? 'nonwork' : ''}`} key={day}>
        <div className="cal-date">{day}</div>
        {bands}
        {b.mile.map((m, mi) => (
          <div
            key={`ml${mi}`}
            className="cal-mile"
            style={{ color: m.color, borderColor: m.color }}
            title={`${m.title}${m.auto ? ' (phase end milestone)' : ''}`}
            onClick={onOpenGovernance}
          >
            ★ {m.title}
          </div>
        ))}
        {b.ev.map((e, ei) => (
          <div
            key={`ev${ei}`}
            className="cal-ev"
            style={{
              borderColor: EV_COLOR[e.event_type] || '#5e35b1',
              color: EV_COLOR[e.event_type] || '#5e35b1',
            }}
            title={e.title}
            onClick={onOpenGovernance}
          >
            📌 {e.title}
          </div>
        ))}
      </div>,
    );
  }

  const monthName = new Date(calY, calM, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <>
      <div className="cal-head">
        <div style={{ fontSize: 13, color: '#78909c' }}>
          Phase boundaries (▶ start / ■ end) &amp; governance events
        </div>
        <div className="cal-nav">
          <button className="cal-btn" onClick={() => onMove(-1)}>
            ‹
          </button>
          <div className="cal-month">{monthName}</div>
          <button className="cal-btn" onClick={() => onMove(1)}>
            ›
          </button>
          <button className="btn-ghost" onClick={onToday} style={{ marginLeft: 8 }}>
            Today
          </button>
        </div>
      </div>
      <div className="ph-legend">
        {phases.map((ph) => (
          <span key={ph.key}>
            <i style={{ background: PHASE_COLORS[ph.key] || '#90a4ae' }} />
            {PHASE_LABEL[ph.key] || ph.label}
          </span>
        ))}
      </div>
      <div
        className="cal-grid"
        /* One month per wheel notch, with a 220ms lock — an unthrottled wheel would skip
           a year in a single gesture. Scrolling UP moves FORWARD, as the source does. */
        onWheel={(e) => {
          e.preventDefault();
          if (wheelLock.current) return;
          wheelLock.current = true;
          setTimeout(() => {
            wheelLock.current = false;
          }, 220);
          onMove(e.deltaY < 0 ? 1 : -1);
        }}
      >
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div className="cal-dow" key={d}>
            {d}
          </div>
        ))}
        {cells}
      </div>
    </>
  );
}

/* ===================== BUDGET / RESOURCE UTILIZATION ===================== */

interface UtilProps {
  kind: 'budget' | 'resources';
  deliveryProjectId: string;
  onOpenTracker: (kind: 'budget' | 'resources') => void;
}

interface UtilPhase {
  phase: string;
  label: string;
  planned?: number;
  actual?: number;
  planned_effort?: number;
  actual_effort?: number;
}

export function EccUtilization({ kind, deliveryProjectId, onOpenTracker }: UtilProps) {
  const [phases, setPhases] = useState<UtilPhase[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const url = kind === 'budget' ? '/budget-tracker/data' : '/resource-tracker/data';
    setPhases(null);
    setFailed(false);
    pmGet<{ success: boolean; phases: UtilPhase[] }>(
      `${url}?delivery_project_id=${encodeURIComponent(deliveryProjectId)}`,
    )
      .then((d) => (d.success ? setPhases(d.phases ?? []) : setFailed(true)))
      .catch(() => setFailed(true));
  }, [kind, deliveryProjectId]);

  if (failed)
    return <div style={{ padding: 40, textAlign: 'center', color: '#e57373' }}>Failed to load.</div>;
  if (!phases)
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#90a4ae' }}>
        Loading {kind === 'budget' ? 'budget' : 'resource'} utilization…
      </div>
    );

  const isB = kind === 'budget';
  const col = isB ? '#1976d2' : '#00897b';
  const lbl = isB ? 'Budget Tracker' : 'Resource Tracker';
  const fmtv = isB
    ? (n: unknown) => `$${Math.round(Number(n) || 0).toLocaleString('en-US')}`
    : (n: unknown) => `${(Math.round((Number(n) || 0) * 10) / 10).toLocaleString()} pd`;
  const get = isB
    ? (r: UtilPhase) => [Number(r.planned) || 0, Number(r.actual) || 0]
    : (r: UtilPhase) => [Number(r.planned_effort) || 0, Number(r.actual_effort) || 0];

  const plannedTotal = phases.reduce((s, r) => s + get(r)[0]!, 0);
  const actualTotal = phases.reduce((s, r) => s + get(r)[1]!, 0);
  const util = plannedTotal > 0 ? Math.round((actualTotal / plannedTotal) * 100) : 0;
  const maxv = Math.max(
    1,
    ...phases.map((r) => {
      const [p, a] = get(r);
      return Math.max(p!, a!);
    }),
  );

  return (
    <div style={{ padding: '18px 20px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 10,
          marginBottom: 16,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 700, color: '#37474f' }}>
          {isB ? 'Budget' : 'Resource'} Utilization by Phase
        </div>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
          {(
            [
              ['Planned', fmtv(plannedTotal), '#263238', false],
              ['Actual', fmtv(actualTotal), '#263238', false],
              ['Utilization', `${util}%`, utilColor(util), true],
            ] as [string, string, string, boolean][]
          ).map(([k, v, c, big]) => (
            <div style={{ textAlign: 'right' }} key={k}>
              <div
                style={{
                  fontSize: 10,
                  textTransform: 'uppercase',
                  color: '#90a4ae',
                  fontWeight: 700,
                }}
              >
                {k}
              </div>
              <div
                style={{
                  fontWeight: big ? 800 : 700,
                  color: c,
                  ...(big ? { fontSize: '1.15rem' } : {}),
                }}
              >
                {v}
              </div>
            </div>
          ))}
        </div>
      </div>

      {phases.length ? (
        phases.map((r) => {
          const [p, a] = get(r);
          const pu = p! > 0 ? Math.round((a! / p!) * 100) : 0;
          const pw = Math.round((p! / maxv) * 100);
          const aw = Math.round((a! / maxv) * 100);
          const pcol = PHASE_COLORS[r.phase] || '#90a4ae';
          return (
            <div style={{ marginBottom: 13 }} key={r.phase}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 12.5,
                  marginBottom: 4,
                }}
              >
                <span style={{ fontWeight: 600, color: '#37474f' }}>{r.label}</span>
                <span style={{ color: utilColor(pu), fontWeight: 700 }}>
                  {pu}% · {fmtv(a)} / {fmtv(p)}
                </span>
              </div>
              <div
                style={{
                  position: 'relative',
                  height: 18,
                  background: '#eceff1',
                  borderRadius: 5,
                  overflow: 'hidden',
                }}
              >
                {/* planned at 30% behind, actual solid in front — both scaled to the largest phase */}
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: `${pw}%`,
                    background: pcol,
                    opacity: 0.3,
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: `${Math.min(100, aw)}%`,
                    background: utilColor(pu),
                    borderRadius: 5,
                  }}
                />
              </div>
            </div>
          );
        })
      ) : (
        <div style={{ color: '#90a4ae', padding: '20px 0' }}>No phase data yet.</div>
      )}

      <div
        style={{
          marginTop: 18,
          paddingTop: 16,
          borderTop: '1px solid #eceff1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <div style={{ fontSize: 12.5, color: '#78909c', maxWidth: 520, lineHeight: 1.5 }}>
          Planned {isB ? 'budget' : 'effort'} comes from the {isB ? 'Budget' : 'Resource'} Plan; actual{' '}
          {isB ? 'spend is logged as vendor payments' : 'effort is logged as entries (FTE × days)'} on
          the full tracker.
        </div>
        <button
          onClick={() => onOpenTracker(kind)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: col,
            color: '#fff',
            border: 'none',
            padding: '8px 16px',
            borderRadius: 7,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <i className="material-icons" style={{ fontSize: 18 }}>
            open_in_new
          </i>{' '}
          Open {lbl}
        </button>
      </div>
    </div>
  );
}

/* ===================== RISK REGISTER ===================== */

interface RiskProps {
  risks: EccRisk[];
  editingRiskId: number;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
}

export function EccRiskList({ risks, editingRiskId, onEdit, onDelete }: RiskProps) {
  const t = today();
  const rows = risks.filter((r) => r.id !== editingRiskId);
  const open = risks.filter((r) => r.status !== 'Resolved' && r.status !== 'Closed').length;

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 14,
        }}
      >
        <div style={{ fontSize: 13, color: '#78909c' }}>
          Risk register — <b>{open}</b> open · owner, resolution due date, mitigation &amp; resolution
        </div>
        <button className="btn-pri" onClick={() => onEdit(0)}>
          + Add Risk
        </button>
      </div>
      {!rows.length ? (
        <div style={{ color: '#b0bec5', fontSize: 13, padding: 10 }}>No risks logged yet.</div>
      ) : (
        rows.map((r) => {
          const sev = r.severity || 'Medium';
          const sc = SEV_COLOR[sev] || '#f9a825';
          const done = r.status === 'Resolved' || r.status === 'Closed';
          const overdue = r.due_date && pd(r.due_date)! < t && !done;
          return (
            <div
              className="gov-row"
              key={r.id}
              style={{
                borderLeft: `4px solid ${sc}`,
                ...(done ? { opacity: 0.62 } : {}),
                alignItems: 'flex-start',
              }}
            >
              <span className="ev-type" style={{ background: `${sc}1a`, color: sc }}>
                {sev}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: '#37474f', fontSize: 13 }}>{r.title}</div>
                <div style={{ fontSize: 11, color: '#90a4ae', marginTop: 1 }}>
                  {r.owner ? `👤 ${r.owner} · ` : ''}
                  {r.due_date ? (
                    <>
                      <span style={{ color: overdue ? '#e53935' : '#607d8b', fontWeight: 600 }}>
                        due {fmtShort(r.due_date)}
                      </span>
                      {' · '}
                    </>
                  ) : null}
                  <span style={{ color: done ? '#43a047' : '#1976d2', fontWeight: 600 }}>{r.status}</span>
                  {r.notes && r.notes.length ? ` · 📝 ${r.notes.length}` : ''}
                </div>
                {r.mitigation ? (
                  <div style={{ fontSize: 11, color: '#546e7a', marginTop: 4 }}>
                    <b style={{ color: '#fb8c00' }}>Mitigation:</b> {r.mitigation}
                  </div>
                ) : null}
                {r.resolution ? (
                  <div style={{ fontSize: 11, color: '#558b2f', marginTop: 2 }}>
                    <b>Resolution:</b> {r.resolution}
                  </div>
                ) : null}
              </div>
              <button className="btn-ghost" onClick={() => onEdit(r.id)}>
                Edit
              </button>
              <span
                style={{ color: '#e57373', cursor: 'pointer', fontWeight: 700 }}
                onClick={() => onDelete(r.id)}
              >
                ✕
              </span>
            </div>
          );
        })
      )}
    </>
  );
}

/* ===================== GOVERNANCE TIMELINE ===================== */

interface GovProps {
  phases: EccPhase[];
  phaseBounds: Record<string, EccBounds>;
  events: EccEvent[];
  onAdd: (presetType?: string) => void;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
}

/**
 * One chronological timeline: auto phase-end milestones (read-only) plus logged events.
 *
 * The TODAY marker is inserted before the first node that is not in the past, and appended
 * at the end when every node is past — so the line always appears exactly once, wherever
 * "now" falls in the sequence.
 */
export function EccGovernance({ phases, phaseBounds, events, onAdd, onEdit, onDelete }: GovProps) {
  const t = today();
  interface Node {
    kind: 'ms' | 'ev';
    date: string;
    title: string;
    type: string;
    color: string;
    ev?: EccEvent;
  }
  const nodes: Node[] = [];
  phases.forEach((ph) => {
    const bd = phaseBounds[ph.key];
    if (bd && bd.end)
      nodes.push({
        kind: 'ms',
        date: bd.end,
        title: `${PHASE_LABEL[ph.key] || ph.key} — phase end`,
        type: 'Milestone',
        color: PHASE_TEXT_C[ph.key] || '#546e7a',
      });
  });
  events.forEach((e) => {
    const isMile = e.event_type === 'Milestone';
    nodes.push({
      kind: isMile ? 'ms' : 'ev',
      date: e.event_date || '',
      title: e.title,
      type: e.event_type,
      color: isMile ? '#455a64' : EV_COLOR[e.event_type] || '#5e35b1',
      ev: e,
    });
  });
  // Undated nodes sort to the end rather than to 1970.
  nodes.sort((a, b) => {
    const da = a.date ? pd(a.date) : null;
    const db = b.date ? pd(b.date) : null;
    if (!da) return 1;
    if (!db) return -1;
    return da.getTime() - db.getTime();
  });

  const out: JSX.Element[] = [];
  let nowPlaced = false;
  const nowLine = (
    <div className="tl-now" key="now">
      <div className="lbl">TODAY</div>
      <div className="line" />
    </div>
  );

  nodes.forEach((n, i) => {
    const dt = n.date ? pd(n.date) : null;
    const past = !!(dt && dt < t);
    if (dt && !past && !nowPlaced) {
      out.push(nowLine);
      nowPlaced = true;
    }
    const dstr = dt ? `${MON3[dt.getMonth()]} ${dt.getDate()}` : '—';
    const yr = dt ? dt.getFullYear() : '';
    const e = n.ev;
    const isRec = !!(e && e.recurrence && e.recurrence !== 'One-time');
    const occ = isRec ? expandOccurrences(e!) : [];
    out.push(
      <div className={`tl-row ${past ? 'past' : ''}`} key={`n${i}`}>
        <div className="when">
          <div className="d">{dstr}</div>
          <div className="yr">{yr}</div>
        </div>
        <div className={`tl-node ${n.kind}`} style={{ ['--c' as string]: n.color }}>
          <span className="mk" />
        </div>
        <div className="tl-card">
          <div className="tc-top">
            <span className="title">{n.title}</span>
            <span className="tl-badge" style={{ background: `${n.color}1c`, color: n.color }}>
              {n.type}
            </span>
            <span className="tl-act">
              {e ? (
                <>
                  <button
                    className="btn-ghost"
                    onClick={() => onEdit(e.id)}
                    style={{ padding: '4px 11px', fontSize: 11.5 }}
                  >
                    Edit
                  </button>
                  <span
                    onClick={() => onDelete(e.id)}
                    style={{ color: '#e57373', cursor: 'pointer', fontWeight: 700 }}
                  >
                    ✕
                  </span>
                </>
              ) : (
                <span className="rd">read‑only</span>
              )}
            </span>
          </div>
          <div className="meta">
            {e ? (
              <>
                <span
                  style={{
                    color: e.status === 'Done' ? '#43a047' : past ? '#e53935' : '#1976d2',
                    fontWeight: 700,
                  }}
                >
                  {e.status}
                </span>
                {isRec
                  ? ` · 🔁 ${e.recurrence}${e.recurrence_end ? ` until ${fmtShort(e.recurrence_end)}` : ''}`
                  : ''}
                {e.notes ? ` · ${e.notes.substring(0, 44)}` : ''}
                {isRec && occ.length ? (
                  <div style={{ marginTop: 5 }}>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 800,
                        textTransform: 'uppercase',
                        color: '#7e57c2',
                      }}
                    >
                      {occ.length} session{occ.length === 1 ? '' : 's'}
                    </span>{' '}
                    {occ.slice(0, 8).map((ds, oi) => (
                      <span
                        className="occ-chip"
                        key={oi}
                        style={pd(ds)! < t ? { opacity: 0.5 } : undefined}
                      >
                        {fmtShort(ds)}
                      </span>
                    ))}
                    {occ.length > 8 ? ' …' : ''}
                  </div>
                ) : null}
              </>
            ) : (
              'Auto from phase end'
            )}
          </div>
        </div>
      </div>,
    );
  });
  if (!nowPlaced) out.push(nowLine);

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 14,
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontSize: 13, color: '#78909c' }}>
          Steering committees, gate reviews, meetings &amp; milestones — one timeline, newest at the
          bottom.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-ghost" onClick={() => onAdd('Milestone')}>
            + Add Milestone
          </button>
          <button className="btn-pri" onClick={() => onAdd()}>
            + Add Event
          </button>
        </div>
      </div>
      <div className="tl">
        {!nodes.length ? (
          <div style={{ color: '#b0bec5', fontSize: 13, padding: 26, textAlign: 'center' }}>
            No milestones or events yet — add one above.
          </div>
        ) : (
          out
        )}
      </div>
    </>
  );
}

/* ===================== AGILE BACKLOG (Agile projects only) ===================== */

interface AgileData {
  mvps: { id: number; name: string }[];
  epics: { id: number; mvp_id: number | null; name: string; status: string }[];
  features: { id: number; epic_id: number; name: string; status: string }[];
  stories: {
    id: number;
    feature_id: number | null;
    epic_id: number | null;
    sprint_id: number | null;
    title: string | null;
    as_a: string | null;
    i_want: string | null;
    story_points: number | null;
    status: string;
  }[];
  sprints: {
    id: number;
    name: string;
    goal: string | null;
    status: string;
    start_date: string | null;
    end_date: string | null;
  }[];
}

/** A status pill, coloured by the shared work-item palette. The source's `bkPill`. */
function BkPill({ status }: { status: string | null }) {
  const c = STATUS_COLOR[status ?? ''] || '#9e9e9e';
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 10,
        fontWeight: 700,
        color: '#fff',
        background: c,
        padding: '1px 8px',
        borderRadius: 10,
        whiteSpace: 'nowrap',
      }}
    >
      {status || '—'}
    </span>
  );
}

/** A story with no title falls back to its "As a … I want …" — the source's `bkStoryLabel`. */
const bkStoryLabel = (s: AgileData['stories'][number]) =>
  s.title || (s.as_a || s.i_want ? `As a ${s.as_a || '…'}, I want ${s.i_want || '…'}` : 'Untitled story');

function BkStoryRow({ s }: { s: AgileData['stories'][number] }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '2px 0 2px 28px', fontSize: 12 }}>
      <span style={{ color: '#2e7d32', fontWeight: 700, fontSize: 10 }}>◇</span>
      <span style={{ flex: 1, color: '#37474f', minWidth: 0 }}>{bkStoryLabel(s)}</span>
      {s.story_points != null ? (
        <span
          style={{
            fontSize: 10.5,
            color: '#607d8b',
            background: '#eceff1',
            padding: '1px 6px',
            borderRadius: 8,
          }}
        >
          {s.story_points}
        </span>
      ) : null}
      <BkPill status={s.status} />
    </div>
  );
}

/**
 * The Agile Backlog tab: Sprint Backlog above, Product Backlog (MVP → Epic → Feature → Story)
 * below. Read-only here — editing lives on the Agile Delivery Workspace, which the header
 * links to.
 *
 * Epics with no MVP fall into a trailing "Not assigned to an MVP" group rather than being
 * dropped, and a group with no epics renders nothing at all.
 */
export function EccBacklog({
  deliveryProjectId,
  onOpenWorkspace,
}: {
  deliveryProjectId: string;
  onOpenWorkspace: () => void;
}) {
  const [A, setA] = useState<AgileData | null>(null);

  useEffect(() => {
    setA(null);
    pmGet<{ success: boolean } & AgileData>(
      `/agile/data?delivery_project_id=${encodeURIComponent(deliveryProjectId)}`,
    )
      .then((d) => (d.success ? setA(d) : setA(null)))
      .catch(() => setA(null));
  }, [deliveryProjectId]);

  if (!A) return <div style={{ padding: 40, textAlign: 'center', color: '#90a4ae' }}>Loading backlog…</div>;

  const { stories = [], sprints = [], epics = [], features = [], mvps = [] } = A;

  const sprintCards = sprints.map((sp) => {
    const ss = stories.filter((s) => s.sprint_id === sp.id);
    const pts = ss.reduce((a, s) => a + (Number(s.story_points) || 0), 0);
    const donePts = ss
      .filter((s) => s.status === 'Done')
      .reduce((a, s) => a + (Number(s.story_points) || 0), 0);
    const dates = sp.start_date || sp.end_date ? `${sp.start_date || '?'} → ${sp.end_date || '?'}` : '';
    return (
      <div
        key={sp.id}
        style={{
          border: '1px solid #e3e8ec',
          borderRadius: 9,
          padding: '12px 14px',
          marginBottom: 10,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            marginBottom: 4,
          }}
        >
          <strong style={{ color: '#263238' }}>{sp.name}</strong>
          <span style={{ fontSize: 11.5, color: '#90a4ae' }}>{dates}</span>
          <BkPill status={sp.status} />
          <span style={{ marginLeft: 'auto', fontSize: 11.5, color: '#607d8b', fontWeight: 600 }}>
            {donePts}/{pts} pts done
          </span>
        </div>
        {sp.goal ? (
          <div style={{ fontSize: 12, color: '#78909c', marginBottom: 2 }}>🎯 {sp.goal}</div>
        ) : null}
        {ss.length ? (
          ss.map((s) => (
            <div
              key={s.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '5px 0',
                borderTop: '1px solid #f0f3f6',
                fontSize: 12.5,
              }}
            >
              <span style={{ flex: 1, color: '#37474f', minWidth: 0 }}>{bkStoryLabel(s)}</span>
              {s.story_points != null ? (
                <span
                  style={{
                    fontSize: 11,
                    color: '#607d8b',
                    background: '#eceff1',
                    padding: '1px 7px',
                    borderRadius: 9,
                  }}
                >
                  {s.story_points} pts
                </span>
              ) : null}
              <BkPill status={s.status} />
            </div>
          ))
        ) : (
          <div style={{ padding: '6px 0', color: '#b0bec5', fontSize: 12 }}>
            No stories committed to this sprint.
          </div>
        )}
      </div>
    );
  });

  const groups: { id: number | null; name: string }[] = [
    ...mvps.map((m) => ({ id: m.id as number | null, name: m.name })),
    { id: null, name: 'Not assigned to an MVP' },
  ];
  const prod = groups
    .map((g) => {
      const ge = epics.filter((e) => (g.id === null ? !e.mvp_id : e.mvp_id === g.id));
      if (!ge.length) return null;
      return (
        <div
          key={String(g.id)}
          style={{
            border: '1px solid #e3e8ec',
            borderRadius: 9,
            padding: '12px 14px',
            marginBottom: 10,
          }}
        >
          <div style={{ fontWeight: 700, color: '#37474f', marginBottom: 2 }}>{g.name}</div>
          {ge.map((ep) => {
            const efeats = features.filter((f) => f.epic_id === ep.id);
            const direct = stories.filter((s) => s.epic_id === ep.id && !s.feature_id);
            return (
              <div style={{ margin: '8px 0' }} key={ep.id}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: '#1a237e' }}>
                  {ep.name} <BkPill status={ep.status} />
                </div>
                {efeats.map((f) => (
                  <div style={{ margin: '4px 0 4px 16px' }} key={f.id}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#6a1b9a' }}>
                      ▸ {f.name} <BkPill status={f.status} />
                    </div>
                    {stories
                      .filter((s) => s.feature_id === f.id)
                      .map((s) => (
                        <BkStoryRow s={s} key={s.id} />
                      ))}
                  </div>
                ))}
                {direct.map((s) => (
                  <BkStoryRow s={s} key={s.id} />
                ))}
              </div>
            );
          })}
        </div>
      );
    })
    .filter(Boolean);

  return (
    <div style={{ padding: '4px 2px' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <h6 style={{ margin: 0, fontWeight: 700, color: '#37474f' }}>Sprint Backlog</h6>
        <button
          onClick={onOpenWorkspace}
          style={{
            marginLeft: 'auto',
            fontSize: 12,
            color: '#1976d2',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Open Agile Delivery Workspace →
        </button>
      </div>
      {sprintCards.length ? (
        sprintCards
      ) : (
        <div style={{ color: '#b0bec5', fontSize: 12.5 }}>No sprints defined.</div>
      )}
      <h6 style={{ margin: '18px 0 10px', fontWeight: 700, color: '#37474f' }}>Product Backlog</h6>
      {prod.length ? prod : <div style={{ color: '#b0bec5', fontSize: 12.5 }}>No epics yet.</div>}
    </div>
  );
}
